import { useMemo } from 'react';

import { State } from 'modules';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { DEFAULT_PAGE_SIZE } from 'constants/common';
import {
  paymentApproveEndpoint,
  paymentBulkResendEndpoint,
  paymentDownloadEndpoint,
  paymentEmailStatusEndpoint,
  paymentEndpoint,
  paymentResendEndpoint,
  paymentSingleEndpoint,
  paymentsExport,
  requestPaymentEndpoint,
  userPaymentDetailsEndpoint,
  userPaymentEndpoint,
} from 'constants/endpoints';

import { appSelectors } from 'selectors';

import type { PaymentExportOptions } from 'features/Payments/PaymentExportModal';

import { getPageNumberAfterLoadMore } from 'utils/helper';
import { httpMethod, simplifyBuilder } from 'utils/sra';

import {
  APIPayment,
  APIPaymentEmailStatus,
  APIPaymentRequest,
  APIPaymentSearchFilter,
  APIPaymentSearchResult,
  APIPaymentStatus,
  APIPaymentUserRequest,
  APISortOrder,
  PrintType,
} from 'types/api';
import { APIPaymentModel } from 'types/backend';

export type PaymentState = {
  paymentList: APIPayment[];
  paymentEmailStatuses: APIPaymentEmailStatus[];
  payment: APIPayment | null;
  loadPaymentPending: boolean;
  loadPaymentsPending: boolean;
  createPaymentPending: boolean;
  allPaymentsLoaded: boolean;
  paymentListLoaded: boolean;
  paymentPending: boolean;
  exportPaymentsPending: boolean;
  loadUserPaymentsPending: boolean;
  loadMoreUserPaymentsPending: boolean;
  paymentEmailStatusPending: boolean;
  requestPaymentPending: boolean;
  filters: APIPaymentSearchFilter;
  pagination: {
    pageNumber: APIPaymentSearchResult['pageNumber'];
    pageSize: APIPaymentSearchResult['pageSize'];
  };
};

export const paymentState: PaymentState = {
  paymentList: [],
  paymentEmailStatuses: [],
  payment: null,
  loadPaymentPending: false,
  paymentPending: false,
  exportPaymentsPending: false,
  loadUserPaymentsPending: false,
  loadMoreUserPaymentsPending: false,
  paymentEmailStatusPending: false,
  requestPaymentPending: false,
  filters: {
    searchSortOrders: [
      {
        sortField: 'createdOn',
        sortOrder: APISortOrder.DESC,
      },
    ],
  },
  loadPaymentsPending: false,
  createPaymentPending: false,
  paymentListLoaded: false,
  allPaymentsLoaded: false,
  pagination: { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
};

const builder = simplifyBuilder(paymentState, {});

const updatePaymentFilters = builder.createReduxAction(
  (filters: APIPaymentSearchFilter) => ({
    name: 'updatePaymentFilters',
    updater: () => ({
      filters,
    }),
  }),
);

export const loadUserPayments = builder.createServerAction(
  (organizationId: string, query: string) => ({
    name: 'loadUserPayments',
    url: userPaymentEndpoint(),
    body: (state: State) => ({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      organizationIds: organizationId ? [organizationId] : undefined,
      query: query,
      ...state.payment.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: PaymentState, payload: APIPaymentSearchResult) => ({
      paymentList: payload.result,
      paymentListLoaded: true,
      allPaymentsLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadMoreUserPayments = builder.createServerAction(
  (organizationId: string, query: string) => ({
    name: 'loadMoreUserPayments',
    url: userPaymentEndpoint(),
    body: (state: State) => ({
      pageNumber: getPageNumberAfterLoadMore(
        state.payment.paymentList,
        state.payment.pagination.pageSize,
      ),
      organizationIds: organizationId ? [organizationId] : undefined,
      query: query,
      ...state.payment.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: PaymentState, payload: APIPaymentSearchResult) => ({
      paymentList: [...(state.paymentList || []), ...payload.result],
      paymentListLoaded: true,
      allPaymentsLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadPayments = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadPayments',
    url: paymentEndpoint(organizationId),
    body: (state: State) => ({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      ...state.payment.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: PaymentState, payload: APIPaymentSearchResult) => ({
      paymentList: payload.result,
      paymentListLoaded: true,
      allPaymentsLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadMorePayments = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadMorePayments',
    url: organizationId
      ? paymentEndpoint(organizationId)
      : userPaymentEndpoint(),
    body: (state: State) => ({
      pageNumber: getPageNumberAfterLoadMore(
        state.payment.paymentList,
        state.payment.pagination.pageSize,
      ),
      ...state.payment.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: PaymentState, payload: APIPaymentSearchResult) => ({
      paymentList: [...(state.paymentList || []), ...payload.result],
      paymentListLoaded: true,
      allPaymentsLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadPayment = builder.createServerAction(
  (organizationId: string, paymentId: string) => ({
    name: 'loadPayment',
    url: paymentSingleEndpoint(organizationId, paymentId),
    method: httpMethod.get,
    onSuccess: (state: PaymentState, payload: APIPayment) => ({
      payment: payload,
    }),
  }),
);

export const loadUserPayment = builder.createServerAction(
  (paymentId: string) => ({
    name: 'loadUserPayment',
    url: userPaymentDetailsEndpoint(paymentId),
    method: httpMethod.get,
    onSuccess: (state: PaymentState, payload: APIPayment) => ({
      payment: payload,
    }),
  }),
);

export const deletePayment = builder.createServerAction(
  (organizationId: string, paymentId: string) => ({
    name: 'deletePayment',
    url: paymentSingleEndpoint(organizationId, paymentId),
    method: httpMethod.delete,
    onSuccess: (state: PaymentState, payload: APIPaymentModel) => {
      if (payload.status === APIPaymentStatus.Draft) {
        return {
          paymentList: state.paymentList.filter(it => it.id !== paymentId),
        };
      }

      return {
        paymentList: state.paymentList.map(it =>
          it.id === paymentId ? { ...it, status: APIPaymentStatus.Void } : it,
        ),
      };
    },
  }),
);

export const updatePayment = builder.createServerAction(
  (organizationId: string, paymentId: string, model: APIPaymentRequest) => ({
    name: 'updatePayment',
    url: paymentSingleEndpoint(organizationId, paymentId),
    method: httpMethod.post,
    body: model,
    onSuccess: (state: PaymentState, result: APIPayment) => ({
      paymentList: state.paymentList.map(payment =>
        payment.id === paymentId ? result : payment,
      ),
    }),
  }),
);

export const createPayment = builder.createServerAction(
  (organizationId: string, model: APIPaymentRequest) => ({
    name: 'createPayment',
    url: paymentEndpoint(organizationId),
    method: httpMethod.put,
    body: model,
    onSuccess: (state: PaymentState, result: APIPayment) => ({
      paymentList: [result, ...state.paymentList],
    }),
  }),
);

const clearPaymentFilters = builder.createReduxAction(() => ({
  name: 'clearPaymentFilters',
  updater: () => ({
    filters: paymentState.filters,
    pagination: paymentState.pagination,
  }),
}));

const downloadPayment = builder.createServerAction(
  (organizationId: string, paymentId: string, type: PrintType) => ({
    name: 'downloadPayment',
    url: paymentDownloadEndpoint(organizationId, paymentId, type),
    method: httpMethod.get,
    // onSuccess: we don't put anything to store, just return result for typeahead
  }),
);

const paymentEmailStatus = builder.createServerAction(
  (organizationId: string, paymentId: string) => ({
    name: 'paymentEmailStatus',
    url: paymentEmailStatusEndpoint(organizationId, paymentId),
    method: httpMethod.get,
    onSuccess: (state: PaymentState, payload: APIPaymentEmailStatus[]) => ({
      paymentEmailStatuses: payload,
    }),
  }),
);

const clearPaymentEmailStatus = builder.createReduxAction(() => ({
  name: 'clearPaymentEmailStatus',
  updater: () => ({
    paymentEmailStatuses: [],
  }),
}));

const clearPayment = builder.createReduxAction(() => ({
  name: 'clearPayment',
  updater: () => ({
    payment: null,
  }),
}));

const clearPaymentList = builder.createReduxAction(() => ({
  name: 'clearPaymentList',
  updater: () => ({
    paymentList: [],
    paymentListLoaded: false,
  }),
}));

const resetPaymentFilters = builder.createReduxAction(() => ({
  name: 'resetPaymentFilters',
  updater: () => ({
    filters: paymentState.filters,
    pagination: paymentState.pagination,
  }),
}));

const exportPayments = builder.createServerAction(
  (organizationId: string, options: PaymentExportOptions) => ({
    name: 'exportPayments',
    url: paymentsExport(organizationId),
    body: (state: State) => ({
      ...state.payment.filters,
      format: options.format,
      columns: options.columns,
      exportPageSize: options.exportPageSize,
      includeTotalsRow: options.includeTotalsRow,
    }),
    method: httpMethod.post,
    responseReader: (response: any) => response.blob(),
  }),
);

const requestPayment = builder.createServerAction(
  (organizationId: string, model: APIPaymentUserRequest) => ({
    name: 'requestPayment',
    url: requestPaymentEndpoint(organizationId),
    body: () => model,
    method: httpMethod.post,
    onSuccess: (state: PaymentState, payload: APIPayment) => ({
      paymentList: [payload, ...state.paymentList],
    }),
  }),
);

const resendPayment = builder.createServerAction(
  (organizationId: string, id: string) => ({
    name: 'resendPayment',
    url: paymentResendEndpoint(organizationId, id),
    method: httpMethod.post,
    onSuccess: (state: PaymentState, payload: APIPayment) => ({
      paymentList: state.paymentList.map(it =>
        it.id === payload.id ? payload : it,
      ),
    }),
  }),
);

const bulkResendPayments = builder.createServerAction(
  (organizationId: string, paymentIds: string[]) => ({
    name: 'bulkResendPayments',
    url: paymentBulkResendEndpoint(organizationId),
    method: httpMethod.post,
    body: () => ({ paymentIds }),
    onSuccess: (state: PaymentState, payload: APIPayment[]) => ({
      paymentList: state.paymentList.map(it => {
        const updated = payload.find(p => p.id === it.id);
        return updated ?? it;
      }),
    }),
  }),
);

const approvePayment = builder.createServerAction(
  (organizationId: string, id: string, checkNumber: string) => ({
    name: 'approvePayment',
    url: paymentApproveEndpoint(organizationId, id),
    params: { checkNumber },
    method: httpMethod.post,
    onSuccess: (state: PaymentState, payload: APIPayment) => ({
      paymentList: state.paymentList.map(it =>
        it.id === payload.id ? payload : it,
      ),
    }),
  }),
);

export const usePayment = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector(appSelectors.organizationId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          updatePaymentFilters,
          clearPaymentFilters,
          clearPayment,
          clearPaymentList,
          loadUserPayment,
          resetPaymentFilters,
          requestPayment,
          clearPaymentEmailStatus,
          paymentEmailStatus: paymentEmailStatus.bind(null, organizationId),
          approvePayment: approvePayment.bind(null, organizationId),
          resendPayment: resendPayment.bind(null, organizationId),
          bulkResendPayments: bulkResendPayments.bind(null, organizationId),
          loadUserPayments: loadUserPayments.bind(null, organizationId),
          loadUserMorePayments: loadMoreUserPayments.bind(null, organizationId),
          exportPayments: exportPayments.bind(null, organizationId),
          downloadPayment: downloadPayment.bind(null, organizationId),
          createPayment: createPayment.bind(null, organizationId),
          updatePayment: updatePayment.bind(null, organizationId),
          deletePayment: deletePayment.bind(null, organizationId),
          loadPayment: loadPayment.bind(null, organizationId),
          loadPayments: loadPayments.bind(null, organizationId),
          loadMorePayments: loadMorePayments.bind(null, organizationId),
        },
        dispatch,
      ),
    [dispatch, organizationId],
  );
};

export const paymentReducer = builder.getReducers();
